/**
 * What each effort store will accept off the disk, and what it refuses.
 *
 * These decoders are the store-shaped half of the fail-closed contract. The
 * kernel codec can only detect the two failures that live in the envelope —
 * bytes that are not JSON, and a version this build does not write yet — because
 * it has no idea what a session is. Everything a *session* can be wrong about
 * has to be checked by somebody who knows the shape, and spec §4.5 rule 7 makes
 * one of those checks load-bearing:
 *
 * > Manual history **rejects any stored value that would require decode-time
 * > normalization** — missing/zero revision, missing `updatedAt`,
 * > `updatedAt < createdAt`, `active > wall` — treating the file as unreadable
 * > and preserving its bytes.
 *
 * The named cases are examples of the general rule, not the whole of it, so
 * `endedAt < startedAt` is refused for the same reason: the constructors clamp
 * it, and a decoder that quietly applied that clamp would hand the user a record
 * saying something they never recorded. Refusing keeps the bytes and says so.
 *
 * The refusal is deliberately *not* a validity judgement about training. It is
 * narrower: would reading this value have changed it? Anything deeper —
 * execution-entry arithmetic, visit overlap, the audit-date ordering against
 * `endedAt` — is `validateEffortSession`'s job on the way *in*, and applying it
 * on the way out would retroactively delete records written by an earlier build
 * whose arithmetic was fixed since.
 *
 * The legacy store is the mirror image and for one precise reason: it is
 * read-only and never written back (F-HS-27). Rule 7 exists to stop a *manual
 * edit* being saved over a value that was silently altered on the way in, and
 * that risk does not exist where nothing is ever saved. So a legacy row is
 * normalized rather than refused, and a row that cannot be normalized into the
 * canonical shape at all is dropped from the read model with its bytes intact.
 */

import type {
  EffortInstant,
  EffortSessionFields,
  Milliseconds,
  OpenEffortCheckpoint,
  SessionReflection,
  StructuredEffortCheckpoint,
} from "@ff/domain";
import { isDeviceAuthority, isEffortOutcome, isEffortSource } from "./enums";

import type { PayloadDecode } from "../kernel";
import type { RecordIndexFields } from "../kernel";

/* ------------------------------------------------------------------------- *
 * Primitive guards
 * ------------------------------------------------------------------------- */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function describe(value: unknown): string {
  return typeof value === "string" ? `"${value}"` : String(value);
}

function shapeFailure<T>(detail: string): PayloadDecode<T> {
  return { ok: false, trigger: "unsupportedShape", detail };
}

/**
 * A value that could only be read by changing it. Separate from
 * {@link shapeFailure} because the two produce different recovery copy: one says
 * the app does not recognize the record, the other says the app would have had
 * to alter what the user recorded (`FAIL_CLOSED_RECOVERY_COPY`).
 */
function normalizationFailure<T>(detail: string): PayloadDecode<T> {
  return { ok: false, trigger: "normalizationRequired", detail };
}

/**
 * Stores whose descriptor declares only `futureSchema` and `corruptBytes` may
 * not report any other trigger — the descriptor is the contract for what a
 * caller has to be able to handle. A checkpoint, reflection or legacy row whose
 * shape this build cannot use is therefore bytes it could not understand, which
 * is what `corruptBytes` says and what its copy promises: left exactly as found.
 */
function unreadableBytes<T>(detail: string): PayloadDecode<T> {
  return { ok: false, trigger: "corruptBytes", detail };
}

/* ------------------------------------------------------------------------- *
 * The structural core, shared by both history stores
 * ------------------------------------------------------------------------- */

/**
 * The fields a session cannot be missing, whichever store it came out of.
 *
 * Scalars are checked exhaustively because every one of them is a number the
 * user is shown or a discriminant the app branches on. Nested structures — the
 * activity snapshot, the executed plan, the execution entries — are checked for
 * their container shape only: a migrated record legitimately carries entry
 * shapes this build no longer produces (`legacyEstimated` fidelity, empty
 * visits), and rejecting those would remove sessions from the user's training
 * record rather than preserve them.
 */
function checkSessionStructure(payload: unknown): PayloadDecode<Record<string, unknown>> {
  if (!isPlainObject(payload)) return shapeFailure("session payload is not an object");

  if (!isNonEmptyString(payload["id"])) return shapeFailure("session has no id");
  if (!isInteger(payload["schemaVersion"])) {
    return shapeFailure("session has no integer schemaVersion");
  }
  if (!isPlainObject(payload["activity"])) {
    return shapeFailure("session has no activity snapshot");
  }
  if (!isEffortSource(payload["source"])) {
    return shapeFailure(`session source ${describe(payload["source"])} is not a known source`);
  }
  if (!isEffortOutcome(payload["outcome"])) {
    return shapeFailure(`session outcome ${describe(payload["outcome"])} is not a known outcome`);
  }

  const provenance = payload["provenance"];
  if (!isPlainObject(provenance)) return shapeFailure("session has no provenance");
  if (!isDeviceAuthority(provenance["deviceAuthority"])) {
    return shapeFailure(
      `session deviceAuthority ${describe(provenance["deviceAuthority"])} is not known`,
    );
  }

  if (!Array.isArray(payload["executionEntries"])) {
    return shapeFailure("session executionEntries is not an array");
  }

  if (!isInteger(payload["startedAt"])) return shapeFailure("session startedAt is not an instant");
  if (!isInteger(payload["endedAt"])) return shapeFailure("session endedAt is not an instant");
  if (!isInteger(payload["createdAt"])) return shapeFailure("session createdAt is not an instant");

  const wall = payload["wallDurationMilliseconds"];
  const active = payload["activeDurationMilliseconds"];
  if (!isInteger(wall)) return shapeFailure("session wallDurationMilliseconds is not an integer");
  if (!isInteger(active)) {
    return shapeFailure("session activeDurationMilliseconds is not an integer");
  }
  if (wall < 0 || active < 0) return shapeFailure("session durations are negative");

  return { ok: true, value: payload };
}

/* ------------------------------------------------------------------------- *
 * Canonical effort sessions
 * ------------------------------------------------------------------------- */

/** Decodes one canonical `EffortSession` row, refusing every normalization. */
export function decodeCanonicalEffortSession(
  payload: unknown,
): PayloadDecode<EffortSessionFields> {
  const structure = checkSessionStructure(payload);
  if (!structure.ok) return structure;
  const fields = structure.value;

  const revision = fields["revision"];
  if (revision === undefined) return normalizationFailure("session has no revision");
  if (!isInteger(revision)) return shapeFailure("session revision is not an integer");
  // Zero is called out by the spec because it is what an absent field decodes to
  // in the native reader; a floor of 1 applied here would invent an edit history.
  if (revision < 1) return normalizationFailure(`session revision is ${revision}`);

  const startedAt = fields["startedAt"] as number;
  const endedAt = fields["endedAt"] as number;
  if (endedAt < startedAt) return normalizationFailure("session endedAt precedes startedAt");

  const wall = fields["wallDurationMilliseconds"] as number;
  const active = fields["activeDurationMilliseconds"] as number;
  if (active > wall) {
    return normalizationFailure(`session active ${active}ms exceeds wall ${wall}ms`);
  }

  const createdAt = fields["createdAt"] as number;
  const updatedAt = fields["updatedAt"];
  if (updatedAt === undefined) return normalizationFailure("session has no updatedAt");
  if (!isInteger(updatedAt)) return shapeFailure("session updatedAt is not an instant");
  if (updatedAt < createdAt) return normalizationFailure("session updatedAt precedes createdAt");

  // Every scalar the app reads has now been checked, and the nested containers
  // are the shapes their own owners validate. The assertion re-states what the
  // checks above established rather than widening anything.
  return { ok: true, value: fields as unknown as EffortSessionFields };
}

/**
 * The scalars copied out of a session so History can order and filter without
 * decoding every record. `planId` is omitted rather than nulled when a session
 * has no origin plan: an index entry for "no plan" would make every planless
 * record share one key, which is a scan wearing an index's name.
 */
export function effortSessionIndex(session: EffortSessionFields): RecordIndexFields {
  const planId = session.originPlan?.id;
  return {
    startedAt: session.startedAt,
    outcome: session.outcome,
    ...(planId === undefined ? {} : { planId }),
  };
}

/* ------------------------------------------------------------------------- *
 * Legacy workout records
 * ------------------------------------------------------------------------- */

/**
 * One row of the legacy store, still untrusted.
 *
 * The legacy store holds records adapted into the canonical shape by the
 * migration pass and carrying a `migration` envelope. It is read-only and never
 * dual-written, so its decoder is deliberately looser than the canonical one: it
 * establishes only that the row is an object, and every question about whether
 * the row can be *used* is answered by {@link adaptLegacyRecord}.
 *
 * That split is F-HS-27's requirement expressed in code. A record that will not
 * decode closes the store (whole-store recovery: one bad history record closes
 * history rather than silently shortening it), while a record that decodes but
 * cannot be adapted is dropped from the read model with its bytes preserved.
 * Collapsing the two would either close history over one unusable legacy row or
 * silently shorten it over a corrupt one.
 */
export type LegacyEffortRow = Readonly<Record<string, unknown>>;

export function decodeLegacyEffortRow(payload: unknown): PayloadDecode<LegacyEffortRow> {
  if (!isPlainObject(payload)) return unreadableBytes("legacy record is not an object");
  return { ok: true, value: payload };
}

/**
 * Adapts one legacy row into the canonical shape, or reports that it cannot.
 *
 * `undefined` means "dropped from the read model": the bytes stay exactly where
 * they are, and the record simply does not appear in history.
 *
 * The normalizations here are the ones the canonical decoder refuses, and they
 * are safe *only* here. A legacy `WorkoutRecord` predates `revision` and
 * `updatedAt` entirely, so refusing a row for lacking them would delete history
 * rather than preserve it — and nothing ever writes this row back, so no edit
 * can be saved over a value that was altered on the way in.
 */
export function adaptLegacyRecord(row: LegacyEffortRow): EffortSessionFields | undefined {
  const structure = checkSessionStructure(row);
  if (!structure.ok) return undefined;
  const fields = structure.value;

  const startedAt = fields["startedAt"] as number;
  const endedAt = Math.max(startedAt, fields["endedAt"] as number);
  const wall = fields["wallDurationMilliseconds"] as number;
  const active = Math.min(wall, fields["activeDurationMilliseconds"] as number);
  const createdAt = fields["createdAt"] as number;

  const storedRevision = fields["revision"];
  const revision = isInteger(storedRevision) ? Math.max(1, storedRevision) : 1;

  const storedUpdatedAt = fields["updatedAt"];
  // A legacy record was never edited, so "last updated" is the moment it ended.
  // Reaching for "now" instead would make the same bytes adapt differently on
  // every read, and History orders and diffs on this value.
  const updatedAt = Math.max(
    createdAt,
    isInteger(storedUpdatedAt) ? storedUpdatedAt : endedAt,
  );

  return {
    ...(fields as unknown as EffortSessionFields),
    revision,
    endedAt: endedAt as EffortInstant,
    activeDurationMilliseconds: active as Milliseconds,
    updatedAt: updatedAt as EffortInstant,
  };
}

/** Legacy rows are ordered by start only; nothing filters them by outcome. */
export function legacyRecordIndex(row: LegacyEffortRow): RecordIndexFields {
  const startedAt = row["startedAt"];
  return isInteger(startedAt) ? { startedAt } : {};
}

/* ------------------------------------------------------------------------- *
 * Session reflections
 * ------------------------------------------------------------------------- */

/**
 * A reflection whose rating is out of range is refused rather than clamped: the
 * rating is the user's own number, and 1…10 is the only range the UI can render.
 * `makeSessionReflection` returns nil for the same input on the way in, so a
 * stored 0 could only have come from bytes this build did not write.
 */
export function decodeSessionReflection(payload: unknown): PayloadDecode<SessionReflection> {
  if (!isPlainObject(payload)) return unreadableBytes("reflection payload is not an object");
  if (!isNonEmptyString(payload["sessionId"])) {
    return unreadableBytes("reflection has no sessionId");
  }
  if (!isInteger(payload["schemaVersion"])) {
    return unreadableBytes("reflection has no integer schemaVersion");
  }

  const createdAt = payload["createdAt"];
  const updatedAt = payload["updatedAt"];
  if (!isInteger(createdAt)) return unreadableBytes("reflection createdAt is not an instant");
  if (!isInteger(updatedAt)) return unreadableBytes("reflection updatedAt is not an instant");
  if (updatedAt < createdAt) return unreadableBytes("reflection updatedAt precedes createdAt");

  const rating = payload["perceivedEffort"];
  if (rating !== undefined && (!isInteger(rating) || rating < 1 || rating > 10)) {
    return unreadableBytes(`reflection perceivedEffort ${describe(rating)} is outside 1…10`);
  }

  const note = payload["note"];
  if (note !== undefined && typeof note !== "string") {
    return unreadableBytes("reflection note is not a string");
  }

  return { ok: true, value: payload as unknown as SessionReflection };
}

export function reflectionIndex(reflection: SessionReflection): RecordIndexFields {
  return { updatedAt: reflection.updatedAt };
}

/* ------------------------------------------------------------------------- *
 * Checkpoints
 * ------------------------------------------------------------------------- */

/**
 * The `shape` discriminant is checked before anything else, because the two
 * checkpoint stores are physically identical singletons: a row that ended up in
 * the wrong one would otherwise decode into a value whose every field is absent
 * and whose type says otherwise.
 *
 * Nothing else here is clamped. The checkpoint self-heals at *initialization*
 * (spec §4.3: segment index into range, remaining into 0…duration, rounds into
 * the plan's) and that clamping is the engine's, applied to a value it is about
 * to resume — clamping here would make the stored bytes and the returned value
 * differ, which is the one thing verified writes exist to prevent.
 */
export function decodeStructuredCheckpoint(
  payload: unknown,
): PayloadDecode<StructuredEffortCheckpoint> {
  if (!isPlainObject(payload)) return unreadableBytes("checkpoint payload is not an object");
  if (payload["shape"] !== "structured") {
    return unreadableBytes(`checkpoint shape ${describe(payload["shape"])} is not "structured"`);
  }
  if (!isNonEmptyString(payload["id"])) return unreadableBytes("checkpoint has no id");
  if (!isNonEmptyString(payload["sessionId"])) return unreadableBytes("checkpoint has no sessionId");
  if (!isInteger(payload["schemaVersion"])) {
    return unreadableBytes("checkpoint has no integer schemaVersion");
  }
  if (!isPlainObject(payload["plan"])) return unreadableBytes("checkpoint has no plan");
  if (!isPlainObject(payload["executionLedger"])) {
    return unreadableBytes("checkpoint has no execution ledger");
  }
  if (!isInteger(payload["segmentIndex"])) {
    return unreadableBytes("checkpoint segmentIndex is not an integer");
  }
  if (typeof payload["isRunning"] !== "boolean" || typeof payload["isFinished"] !== "boolean") {
    return unreadableBytes("checkpoint run state is not boolean");
  }
  return { ok: true, value: payload as unknown as StructuredEffortCheckpoint };
}

export function decodeOpenEffortCheckpoint(
  payload: unknown,
): PayloadDecode<OpenEffortCheckpoint> {
  if (!isPlainObject(payload)) return unreadableBytes("checkpoint payload is not an object");
  if (payload["shape"] !== "duration") {
    return unreadableBytes(`checkpoint shape ${describe(payload["shape"])} is not "duration"`);
  }
  if (!isNonEmptyString(payload["id"])) return unreadableBytes("checkpoint has no id");
  if (!isNonEmptyString(payload["sessionId"])) return unreadableBytes("checkpoint has no sessionId");
  if (!isInteger(payload["schemaVersion"])) {
    return unreadableBytes("checkpoint has no integer schemaVersion");
  }
  if (!isPlainObject(payload["activity"])) return unreadableBytes("checkpoint has no activity");
  if (!isPlainObject(payload["provenance"])) return unreadableBytes("checkpoint has no provenance");
  if (
    !isInteger(payload["activeDurationMilliseconds"]) ||
    !isInteger(payload["wallDurationMilliseconds"])
  ) {
    return unreadableBytes("checkpoint durations are not integers");
  }
  if (typeof payload["isRunning"] !== "boolean" || typeof payload["isFinished"] !== "boolean") {
    return unreadableBytes("checkpoint run state is not boolean");
  }
  return { ok: true, value: payload as unknown as OpenEffortCheckpoint };
}
